import {ChangeDetectorRef, Component, OnDestroy, OnInit, signal, WritableSignal, ChangeDetectionStrategy, inject} from '@angular/core';
import {TitleComponent} from '@/app/shared/components/title/title.component';
import {NgClass} from '@angular/common';
import {Game} from '@/app/core/models/game';
import {ToggleComponent} from '@/app/shared/components/toggle/toggle.component';
import {MatchService} from '@services/match.service';
import {PageSelectorComponent} from '@/app/shared/components/page-selector/page-selector.component';
import {NoUpcomingMatchComponent} from '@components/no-upcoming-match/no-upcoming-match.component';
import {Title} from '@angular/platform-browser';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LastVideosComponent} from '@components/last-videos/last-videos.component';
import {CalendarGroupByDate} from '@/app/core/models/calendar/calendarGroupByDate';
import {MatchsContainerComponent} from '@components/matchs-container/matchs-container.component';
import {EventType, EventTypeList} from '@/app/core/models/calendar/eventCalendar';
import {EventFilterComponent} from '@/app/shared/components/event-filter/event-filter.component';
import {JsonLdService} from '@/app/core/seo/json-ld.service';
import {buildUpcomingEventsJsonLd} from '@/app/core/seo/sports-event.mapper';
import {TranslateDynamicService} from '@services/translate-dynamic.service';
import {environment} from '@/environments/environment';

@Component({
	selector: 'app-matchs',
	imports: [
		TitleComponent,
		ToggleComponent,
		NgClass,
		PageSelectorComponent,
		NoUpcomingMatchComponent,
		TranslatePipe,
		LastVideosComponent,
		MatchsContainerComponent,
		EventFilterComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './matchs.component.html',
})
export class MatchsComponent implements OnInit, OnDestroy {

	/** Clé du bloc JSON-LD posé dans <head> par cette page. */
	private static readonly JSON_LD_SLOT = 'matchs-upcoming';

	private readonly jsonLd = inject(JsonLdService);
	private readonly translateDynamicService = inject(TranslateDynamicService);

	constructor(
		private readonly matchService: MatchService,
		private readonly cdr: ChangeDetectorRef,
		private readonly titleService: Title,
		private readonly translate: TranslateService
	) { }

	protected readonly Game = Game;

	selectedTypeOfMatch: 'upcoming' | 'result' = 'upcoming';

	isShowingScore: boolean = false;

	matchsUpcoming: CalendarGroupByDate[] = [];
	matchsResult: CalendarGroupByDate[] = [];
	filteredMatches: CalendarGroupByDate[] = [];

	page: number = 0;
	totalPages: number = 0;
	selectedEvents: WritableSignal<EventType[]> = signal(EventTypeList);

	ngOnInit(): void {
		this.loadUpcomingMatches();
	}

	ngOnDestroy(): void {
		// Sans ça, le balisage resterait dans <head> après une navigation SPA
		// et décrirait des évènements absents de la page affichée.
		this.jsonLd.remove(MatchsComponent.JSON_LD_SLOT);
	}

	loadUpcomingMatches(): void {
		this.matchService.getUpcomingMatches(this.page, this.selectedEvents().toSorted((a, b) => a.localeCompare(b))).subscribe({
			next: (matches) => {
				this.matchsUpcoming = matches.content;
				this.totalPages = matches.totalPages;
				this.updateFilteredMatches();
				this.publishUpcomingEventsJsonLd();
			}
		});
	}

	/**
	 * Publie le balisage SportsEvent des prochains matchs.
	 *
	 * S'appuie sur `matchsUpcoming` (liste renvoyée par l'API) et non sur
	 * `filteredMatches` : les filtres sont une préférence d'affichage propre au
	 * visiteur, ils n'ont pas à modifier ce que décrit le balisage.
	 */
	private publishUpcomingEventsJsonLd(): void {
		if (this.page !== 0) {
			// Le balisage ne décrit que la première page : au-delà, on le retire
			// plutôt que d'annoncer des évènements que la page n'affiche plus.
			this.jsonLd.remove(MatchsComponent.JSON_LD_SLOT);
			return;
		}

		const jsonLd = buildUpcomingEventsJsonLd(this.matchsUpcoming, {
			lang: this.translateDynamicService.getLang(),
			siteUrl: environment.siteUrl,
			minioBaseUrl: environment.minioBaseUrl,
			pageUrl: `${environment.siteUrl}/matchs`,
		});

		this.jsonLd.set(MatchsComponent.JSON_LD_SLOT, jsonLd);
	}

	loadResultMatches(): void {
		this.matchService.getResultMatches(this.page, this.selectedEvents().toSorted((a, b) => a.localeCompare(b))).subscribe({
			next: (matches) => {
				this.matchsResult = matches.content;
				this.totalPages = matches.totalPages;
				this.updateFilteredMatches();
			}
		});
	}

	updateFilteredMatches(): void {
		if(this.selectedTypeOfMatch === 'result') {
			this.filteredMatches = this.matchsResult
				.map(group => {
					const filtered = group.eventsCalendar.filter(eventCalendar =>
						this.selectedEvents().includes(eventCalendar.eventType!)
					);
					return {
						date: group.date,
						eventsCalendar: filtered
					};
				})
				.filter(group => group.eventsCalendar.length > 0);
		} else{
			this.filteredMatches = this.matchsUpcoming
				.map(group => {
					const filtered = group.eventsCalendar.filter(eventCalendar =>
						this.selectedEvents().includes(eventCalendar.eventType!)
					);
					return {
						date: group.date,
						eventsCalendar: filtered
					};
				})
				.filter(group => group.eventsCalendar.length > 0);
		}

	}

	updateEventTypeFilter(eventType: EventType): void {
		this.selectedEvents.update(list =>
			list.includes(eventType) ? list.filter(r => r !== eventType) : [...list, eventType]
		);
		if(this.selectedEvents().length === 0) {
			this.selectedEvents.set(['OVERWATCH_PRO']);
		}

		if (this.selectedTypeOfMatch === 'result') {
			this.loadResultMatches();
		} else {
			this.loadUpcomingMatches();
		}
	}

	toggleTypeOfMatch(type: 'upcoming' | 'result'): void {
		this.selectedTypeOfMatch = type;
		this.page = 0;
		if(type === 'result') {
			this.loadResultMatches();
		} else {
			this.loadUpcomingMatches();
		}

		this.updateFilteredMatches();
	}

	toggleScoreVisibility(isChecked: boolean): void {
		this.isShowingScore = isChecked;
		this.cdr.detectChanges();
	}

	onPageChange(newPage: number) {
		this.page = newPage;
		if(this.selectedTypeOfMatch === 'upcoming') {
			this.loadUpcomingMatches();
		} else {
			this.loadResultMatches();
		}
	}
}
